import type React from "react"

import type { User, Session } from "@supabase/supabase-js"
import { createContext, useContext, useEffect, useState } from "react"
import supabase, { getAuthRedirectURL } from "../config/supabaseConfig"
import { isRepeatedSignup, REPEATED_SIGNUP_MESSAGE } from "../utils/authHelpers"

// Type for regular user registration data
type UserRegistrationData = {
  email: string
  name: string
  phone: string
  is_verified: boolean
  is_online: boolean
  photo_url: string
}

interface AuthContextType {
  user: User | null
  session: Session | null
  isLoading: boolean
  error: string | null
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  signUp: (email: string, password: string, userData: UserRegistrationData) => Promise<{ user: User | null; session: Session | null } | undefined>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    const initializeAuth = async () => {
      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession()

        if (mounted) {
          setSession(currentSession)
          setUser(currentSession?.user ?? null)
        }
      } catch (err) {
        console.error("Regular user auth initialization error:", err)
        if (mounted) {
          setSession(null)
          setUser(null)
        }
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, newSession) => {

      if (!mounted) return

      setSession(newSession)
      setUser(newSession?.user ?? null)
      setIsLoading(false)

      // Clear any previous errors on successful auth
      if (newSession) {
        setError(null)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signUp = async (email: string, password: string, userData: UserRegistrationData) => {
    try {
      setError(null)
      setIsLoading(true)

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone,
            photo_url: userData.photo_url,
            is_online: userData.is_online,
            is_verified: userData.is_verified,
            user_type: "regular", // Mark as regular user
          },
          emailRedirectTo: getAuthRedirectURL('auth/callback'),
        },
      })

      if (signUpError) {
        setError(signUpError.message)
        throw signUpError
      }

      if (isRepeatedSignup(data?.user)) {
        const repeatedError = new Error(REPEATED_SIGNUP_MESSAGE)
        setError(REPEATED_SIGNUP_MESSAGE)
        throw repeatedError
      }

      // Don't set user here - wait for email verification
      return data
    } catch (err) {
      console.error("Regular user signup error:", err)
      setError(err instanceof Error ? err.message : "An unexpected error occurred")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      setError(null)
      setIsLoading(true)

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (signInError) {
        setError(signInError.message)
        throw signInError
      }

      // Verify this is a regular user, not an admin
      if (data?.user) {
        const userType = data.user.user_metadata?.user_type
        if (userType === "admin") {
          await supabase.auth.signOut()
          setError("Please use the admin login for admin accounts")
          throw new Error("Please use the admin login for admin accounts")
        }

      }
    } catch (err) {
      console.error("Regular user sign in error:", err)
      setError(err instanceof Error ? err.message : "An unexpected error occurred during sign in")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    try {
      setError(null)
      setIsLoading(true)

      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: getAuthRedirectURL('auth/callback'),
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      })

      if (signInError) {
        setError(signInError.message)
        throw signInError
      }
    } catch (err) {
      console.error("Google sign in error:", err)
      setError("An unexpected error occurred during Google sign in")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setIsLoading(true)
      const { error: signOutError } = await supabase.auth.signOut()

      if (signOutError) {
        setError(signOutError.message)
        throw signOutError
      }

      // Clear state immediately
      setUser(null)
      setSession(null)
    } catch (err) {
      console.error("Sign out error:", err)
      setError("An unexpected error occurred during sign out")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const value = {
    user,
    session,
    isLoading,
    error,
    signIn,
    signInWithGoogle,
    signOut,
    signUp,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
